import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WiganCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Wigan Pothole Claims | GREEN Rated, 16.4% U-Roads in RED | Fixtyer"
        description="Wigan Council earns GREEN DfT ratings but 16.4% of unclassified roads are in RED condition, 18,900 potholes were filled in five years, and best practice is AMBER. See what the data means for your claim."
        canonical="/councils/wigan"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Wigan', url: '/councils/wigan' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Does Wigan\'s GREEN DfT rating hurt my pothole claim?',
            answer: 'It changes your approach, but it does not eliminate your claim. You cannot argue systematic failure against a council rated GREEN on condition and spend — but you can cite Wigan\'s own data showing 16.4% of unclassified roads in RED condition (up from 13.1% in 2023), 18,900 potholes filled in five years, and the council\'s admission that U-roads are surveyed less regularly than classified roads. Section 58 turns on the specific defect and inspection record, not the network-level scorecard.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 829.32km — 76.5% of Wigan\'s 1,084.46km carriageway network. At the latest survey, 16.4% were in RED condition. The council deploys SCANNER surveys on U-roads on a rotating basis every three to four years, while classified A, B and C roads are surveyed annually in both directions. Visual inspections on U-roads introduce subjectivity, which the council itself acknowledges.',
          },
          {
            question: 'Why is Wigan AMBER on best practice when everything else is GREEN?',
            answer: 'The DfT Best Practice scorecard is AMBER despite GREEN ratings on condition and spend. Wigan\'s own report admits survey frequency on unclassified roads produces outdated or incomplete data, visual inspections introduce inconsistency, and temporary patch repairs may mask deeper structural problems. The council is still developing AI-powered surveys and predictive analytics — but those tools are described as future investments, not current practice.',
          },
          {
            question: 'Wigan fills 3,700–4,000 potholes a year — does that mean the roads are fixed?',
            answer: 'No. Wigan filled 18,900 potholes over five years while U-road RED condition peaked at 18.6% in 2022 and returned to 16.4% in 2024 after a brief dip to 13.1%. Reactive pothole filling is evidence of ongoing deterioration, not proof the underlying network is in good condition. The council expects to fill 3,700–4,000 potholes in 2025/26 as well.',
          },
          {
            question: 'Wigan matches its DfT capital allocation exactly — can I still claim?',
            answer: 'Yes. Wigan spent £6,917,000 of capital against a £6,917,000 DfT allocation in 2024/25, which is why Spend is GREEN. But capital spend on resurfacing has fallen from 33 miles in 2020/21 to a planned 14.3 miles in 2025/26, and 14% of maintenance spend is still reactive. Your claim is decided on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 of the Highways Act 1980 — not on whether the council matched its DfT cheque.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Wigan Council"
        councilSlug="wigan"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/wigan"
        headline="Wigan Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Wigan</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Wigan: Green-Rated, Residential Roads Still in RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Wigan Council earns <strong className="text-emerald-400">GREEN</strong> DfT scorecards on condition
            and spend, matches its capital allocation pound for pound, and runs an 86% preventative maintenance
            programme. Yet best practice is <strong className="text-amber-400">AMBER</strong>,{' '}
            <strong className="text-rose-400">16.4% of unclassified roads are in RED condition</strong> on a
            network where 829km — three-quarters of carriageway — is residential, and the council filled{' '}
            <strong className="text-rose-400">18,900 potholes in five years</strong>.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">16.4%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 13.1% in 2023 after peaking at 18.6% in 2022 — roughly 136km of Wigan&apos;s 829km
              unclassified network, where SCANNER surveys run on a three-to-four-year rotation.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network size */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A 1,084km Network, Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Wigan Council&apos;s Local Highways Maintenance Transparency Report 2025
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">142.18</td>
                  <td className="px-6 py-4 text-center">13.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center">112.96</td>
                  <td className="px-6 py-4 text-center">10.4%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">U roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-bold">829.32</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">76.5%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-bold">1,084.46</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-600 text-sm">
            Wigan also maintains 1,765.40km of footways, 182km of cycleways, 500km of public rights of way,
            and 489 structures across the borough.
          </p>
        </div>
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-8">
            The DfT&apos;s Verdict
          </h2>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">Wigan Council DfT Road Maintenance Ratings 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Metric</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall Rating</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Road Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Highway Maintenance Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl mb-4">
            <p className="text-emerald-800">
              <strong>What GREEN means:</strong> GREEN does not mean problem-free. It means Wigan performs
              above average compared to other councils on condition and spend. Classified A and B/C roads
              show low RED percentages — 2.4% and 2.1% respectively in 2024. The DfT scorecard is a
              network-level assessment, not a guarantee about any individual pothole.
            </p>
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-amber-800">
              <strong>The AMBER asterisk:</strong> Best Practice is AMBER — the one scorecard that is not
              GREEN. Wigan&apos;s own report admits limitations in how unclassified roads are monitored,
              describes AI surveys as something it is &ldquo;increasingly looking into,&rdquo; and acknowledges
              that patch repairs can mask deeper structural problems. That gap between aspiration and
              current practice is exactly what the DfT Best Practice scorecard captures.
            </p>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of condition data from Wigan&apos;s own transparency report — classified roads stable,
            unclassified roads fluctuating
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (142.18km — 13.1% of network): stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2.4%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">range 1.9–3.3% since 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">19.0%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">78.6%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">100% surveyed annually</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: main roads are in good shape. But A-roads are just one-eighth of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">B and C roads (112.96km — 10.4% of network): stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2.1%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">range 1.5–2.4% since 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">15.9%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">82.0%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">100% surveyed annually</div>
              </div>
            </div>
            <p className="text-emerald-800 text-sm">
              This is the data that drives Wigan&apos;s GREEN condition scorecard — low RED percentages on classified roads.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (829.32km — 76.5% of network): fluctuating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-amber-900">Year</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-amber-900">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-amber-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">15.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">16.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">18.6%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                    <td className="px-6 py-4 text-center font-bold text-emerald-600">13.1%</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">16.4%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              At 16.4% in 2024, roughly 136km of residential streets, estate roads and village routes are
              in the worst condition category — up from 13.1% the year before. This is where most pothole
              claims originate, and it is not fully captured by the GREEN network scorecard.
            </p>
          </div>
        </div>
      </section>

      {/* U-road monitoring */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 829km Monitoring Gap
          </h2>
          <p className="text-surface-600 mb-8">
            How Wigan surveys unclassified roads — and what the council admits about data reliability
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Survey frequency on unclassified roads result in them being assessed less regularly than classified ones, leading to outdated or incomplete data."
              source="Wigan Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="However, by 2024, the proportion increased again to 16.4%, indicating a resurgence of condition issues that will need to be considered as Wigan's future planned maintenance programs are scheduled."
              source="Wigan Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">How U-Roads Are Monitored</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>Visual inspections</strong> — conducted annually by trained inspectors using mobile apps</li>
                <li>• <strong>Condition surveys</strong> — SCANNER or similar vehicles on a rotating basis every three to four years</li>
                <li>• <strong>Community reporting</strong> — residents report defects via an online portal</li>
              </ul>
              <p className="text-surface-600 text-sm mt-3">
                Classified A, B and C roads get 100% SCANNER coverage every year. U-roads do not.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What Distorts The Data</h3>
              <p className="text-amber-800 text-sm">
                Wigan&apos;s own report lists subjectivity in visual inspections, seasonal weather effects,
                equipment calibration issues, and the fact that &ldquo;temporary roadworks or patch repairs may
                give a misleading impression of road quality, masking deeper structural problems.&rdquo;
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, Wigan must show it had a reasonable system for
              knowing the condition of its roads. For the 829km unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last SCANNER-surveyed — or only visually inspected?</li>
              <li>• If 16.4% of U-roads were RED at the last survey, what was done about yours?</li>
              <li>• Could a patch repair have masked the defect that damaged your vehicle?</li>
              <li>• Was your pothole reported via the community portal — and if so, how long before it was repaired?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits its U-road data can be outdated or incomplete faces a harder Section 58
              argument on the specific residential road where your damage occurred.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            18,900 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from Wigan&apos;s own transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">3,600</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,300</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,300</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">3,900</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">3,800</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18,900</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~10 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Wigan fills around 10 potholes per day across its 1,084km network.
                The council estimates 3,700–4,000 potholes will be filled in 2025/26 — the same order of
                magnitude as every recent year. Reactive repair at this scale means defects routinely form
                between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Prevention vs Cure</h3>
              <p className="text-amber-800 text-sm">
                The council states it &ldquo;proactively aims for the method of &lsquo;prevention is better than
                cure&apos;&rdquo; — yet 14% of 2025/26 spend is still projected as reactive, and the council
                estimates 3,700–4,000 potholes will be filled in 2025/26 alongside planned improvements to
                at least 42 footways and 4 structures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following The Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Wigan matches its DfT capital allocation exactly and maintains a high preventative share — but
            resurfacing mileage has halved since 2020/21
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 p-6 mb-8 overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Wigan highway maintenance spending 2020 to 2026</caption>
              <thead>
                <tr className="border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">£6,605,433</td>
                  <td className="px-4 py-3 text-center">£6,605,433</td>
                  <td className="px-4 py-3 text-center">£1,173,000</td>
                  <td className="px-4 py-3 text-center text-emerald-400">87%</td>
                  <td className="px-4 py-3 text-center">13%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">£6,478,000</td>
                  <td className="px-4 py-3 text-center">£6,478,000</td>
                  <td className="px-4 py-3 text-center">£1,284,680</td>
                  <td className="px-4 py-3 text-center text-emerald-400">89%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">£6,478,000</td>
                  <td className="px-4 py-3 text-center">£6,478,000</td>
                  <td className="px-4 py-3 text-center">£1,284,680</td>
                  <td className="px-4 py-3 text-center text-emerald-400">89%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">£7,641,000</td>
                  <td className="px-4 py-3 text-center">£7,641,000</td>
                  <td className="px-4 py-3 text-center">£1,284,680</td>
                  <td className="px-4 py-3 text-center text-emerald-400">87%</td>
                  <td className="px-4 py-3 text-center">13%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">£6,917,000</td>
                  <td className="px-4 py-3 text-center">£6,917,000</td>
                  <td className="px-4 py-3 text-center">£1,284,680</td>
                  <td className="px-4 py-3 text-center text-emerald-400">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
                <tr className="bg-brand-800/50">
                  <td className="px-4 py-3 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-medium">£7,040,000</td>
                  <td className="px-4 py-3 text-center font-medium">£7,040,000</td>
                  <td className="px-4 py-3 text-center">£1,284,680</td>
                  <td className="px-4 py-3 text-center text-emerald-400">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-white mb-3">Resurfacing Mileage Falling</h3>
              <table className="w-full text-sm text-brand-200">
                <tbody>
                  <tr className="border-b border-brand-800">
                    <td className="py-2">2020/21</td>
                    <td className="py-2 text-right font-medium text-white">33 miles</td>
                  </tr>
                  <tr className="border-b border-brand-800">
                    <td className="py-2">2021/22</td>
                    <td className="py-2 text-right">19.6 miles</td>
                  </tr>
                  <tr className="border-b border-brand-800">
                    <td className="py-2">2022/23</td>
                    <td className="py-2 text-right">16.8 miles</td>
                  </tr>
                  <tr className="border-b border-brand-800">
                    <td className="py-2">2023/24</td>
                    <td className="py-2 text-right">18.8 miles</td>
                  </tr>
                  <tr className="border-b border-brand-800">
                    <td className="py-2">2024/25</td>
                    <td className="py-2 text-right">16.6 miles</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium text-amber-400">2025/26 (planned)</td>
                    <td className="py-2 text-right font-bold text-amber-400">14.3 miles</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">The Translation</h3>
              <p className="text-brand-200 text-sm">
                Wigan spent £6,917,000 of capital against a £6,917,000 DfT allocation in 2024/25 — a
                perfect match that earns the GREEN spend scorecard. But planned resurfacing has fallen from
                33 miles to 14.3 miles over five years on a 1,084km network. Preventative spend is high
                as a percentage, but the absolute volume of structural renewal is declining.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Claiming against GREEN council */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Rated Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Wigan is not Derbyshire — here&apos;s how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — classified roads in good shape
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — matches DfT capital allocation every year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 86–89% preventative maintenance share over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% annual SCANNER coverage on A, B and C roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented Highway Asset Management Strategy and GMRAPS coordination
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — survey gaps and future-tense innovation plans
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 16.4% of U-roads in RED condition — roughly 136km of residential network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road SCANNER surveys only every three to four years on rotation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits U-road data can be outdated, incomplete, or distorted by patches
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 18,900 potholes filled in five years — defects form between inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing halved from 33 miles to 14.3 miles planned
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN condition and spend scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong> under Section 41 and Section 58 of the
              Highways Act 1980:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council portal) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the three-to-four-year SCANNER gap is your strongest structural argument</li>
              <li>• Whether a patch repair masked a deeper structural failure the council&apos;s own report warns about</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Wigan&apos;s own transparency data where it helps you — without pretending the council is failing
              when the DfT says otherwise.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Wigan?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road SCANNER-gap argument</li>
                <li>• No prior-report search</li>
                <li>• Ignore the GREEN rating context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 16.4% U-road RED condition documented</li>
                <li>✅ Three-to-four-year survey gap argued</li>
                <li>✅ 18,900 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Wigan — honest about GREEN ratings</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=wigan"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Wigan Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Wigan&apos;s GREEN DfT rating hurt my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It changes your approach, but it does not eliminate your claim. You cannot argue systematic
                failure against a council rated GREEN on condition and spend — but you can cite Wigan&apos;s own
                data showing 16.4% of unclassified roads in RED condition (up from 13.1% in 2023), 18,900
                potholes filled in five years, and the council&apos;s admission that U-roads are surveyed less
                regularly than classified roads. Section 58 turns on the specific defect and inspection record,
                not the network-level scorecard.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 829.32km — 76.5% of Wigan&apos;s 1,084.46km carriageway network. At the latest
                survey, 16.4% were in RED condition. The council deploys SCANNER surveys on U-roads on a
                rotating basis every three to four years, while classified A, B and C roads are surveyed
                annually in both directions. Visual inspections on U-roads introduce subjectivity, which
                the council itself acknowledges.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Wigan AMBER on best practice when everything else is GREEN?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Best Practice scorecard is AMBER despite GREEN ratings on condition and spend. Wigan&apos;s
                own report admits survey frequency on unclassified roads produces outdated or incomplete data,
                visual inspections introduce inconsistency, and temporary patch repairs may mask deeper
                structural problems. The council is still developing AI-powered surveys and predictive
                analytics — but those tools are described as future investments, not current practice.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wigan fills 3,700–4,000 potholes a year — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Wigan filled 18,900 potholes over five years while U-road RED condition peaked at 18.6%
                in 2022 and returned to 16.4% in 2024 after a brief dip to 13.1%. Reactive pothole filling is
                evidence of ongoing deterioration, not proof the underlying network is in good condition. The
                council expects to fill 3,700–4,000 potholes in 2025/26 as well.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wigan matches its DfT capital allocation exactly — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Wigan spent £6,917,000 of capital against a £6,917,000 DfT allocation in 2024/25, which
                is why Spend is GREEN. But capital spend on resurfacing has fallen from 33 miles in 2020/21 to
                a planned 14.3 miles in 2025/26, and 14% of maintenance spend is still reactive. Your claim is
                decided on whether the specific defect that damaged your vehicle was reasonably inspected and
                repaired under Section 58 of the Highways Act 1980 — not on whether the council matched its
                DfT cheque.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.wigan.gov.uk/Docs/PDF/Resident/Parking-Roads-Travel/Roads/Wigan-Council-Highway-Transparency-Report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Wigan Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
